import { createPortal } from 'react-dom';
import type { PendingAnnotation } from '@/lib/desktop-shell/store';
import {
  SELECTION_TOOLBAR_BUTTON_CLASS,
  SELECTION_TOOLBAR_CLASS,
  SELECTION_TOOLBAR_DIVIDER_BUTTON_CLASS,
} from '@/lib/side-panel/constants';
import { type SelectedText, selectionToolbarStyle } from '@/lib/side-panel/text-selection';

/** A captured text selection inside an artifact, with its screen bounds. */
export type TextSelection = SelectedText;
export type FileSelection = TextSelection;
export type AnnotateSelectionInput = PendingAnnotation;

type AnnotationSource = Pick<PendingAnnotation, 'sourceId' | 'sourceKind' | 'sourceLabel'>;

/**
 * Shared floating toolbar for annotating selected text in any Crowdex
 * artifact. Contexts can opt into the side-chat actions used by files, while
 * plans keep only the focused feedback action.
 */
export function SelectionToolbar({
  annotationLabel = 'Add to chat',
  ariaLabel,
  selection,
  source,
  onAnnotateSelection,
  onMoreDetailsSelection,
  onAskSelectionInSideChat,
  onDismiss,
}: {
  annotationLabel?: string;
  ariaLabel: string;
  selection: TextSelection;
  source: AnnotationSource;
  onAnnotateSelection: (input: PendingAnnotation) => void;
  onMoreDetailsSelection?: (content: string) => void;
  onAskSelectionInSideChat?: (content: string) => void;
  onDismiss: () => void;
}) {
  const hasSecondaryActions =
    onMoreDetailsSelection !== undefined || onAskSelectionInSideChat !== undefined;
  const finish = (): void => {
    onDismiss();
    window.getSelection()?.removeAllRanges();
  };

  return createPortal(
    <div
      aria-label={ariaLabel}
      className={SELECTION_TOOLBAR_CLASS}
      data-selection-toolbar
      onMouseDown={(event) => event.preventDefault()}
      role="toolbar"
      style={selectionToolbarStyle(selection.bounds, hasSecondaryActions ? 430 : 180)}
    >
      <button
        className={
          hasSecondaryActions
            ? SELECTION_TOOLBAR_DIVIDER_BUTTON_CLASS
            : SELECTION_TOOLBAR_BUTTON_CLASS
        }
        onClick={() => {
          const browserSelection = window.getSelection();
          const selectedRange =
            browserSelection !== null && browserSelection.rangeCount === 1
              ? browserSelection.getRangeAt(0)
              : null;
          onAnnotateSelection({
            anchor: { x: selection.bounds.left, y: selection.bounds.bottom + 8 },
            anchorRange:
              selectedRange !== null && typeof selectedRange.cloneRange === 'function'
                ? selectedRange.cloneRange()
                : undefined,
            content: selection.text,
            ...source,
          });
          finish();
        }}
        type="button"
      >
        {annotationLabel}
      </button>
      {onMoreDetailsSelection !== undefined && (
        <button
          className={
            onAskSelectionInSideChat === undefined
              ? SELECTION_TOOLBAR_BUTTON_CLASS
              : SELECTION_TOOLBAR_DIVIDER_BUTTON_CLASS
          }
          onClick={() => {
            onMoreDetailsSelection(selection.text);
            finish();
          }}
          type="button"
        >
          More details
        </button>
      )}
      {onAskSelectionInSideChat !== undefined && (
        <button
          className={SELECTION_TOOLBAR_BUTTON_CLASS}
          onClick={() => {
            onAskSelectionInSideChat(selection.text);
            finish();
          }}
          type="button"
        >
          Ask in side chat
        </button>
      )}
    </div>,
    document.body,
  );
}

/**
 * Floating action toolbar for a text selection in the file viewer, portaled to
 * the document body so it floats above the panel. Offers "Add to chat" (as an
 * annotation anchored at the selection), "More details", and "Ask in side chat".
 */
export function FileSelectionToolbar({
  selection,
  filePath,
  onAnnotateSelection,
  onMoreDetailsSelection,
  onAskSelectionInSideChat,
  onDismiss,
}: {
  selection: FileSelection;
  filePath: string;
  onAnnotateSelection: (input: PendingAnnotation) => void;
  onMoreDetailsSelection: (content: string) => void;
  onAskSelectionInSideChat: (content: string) => void;
  onDismiss: () => void;
}) {
  return (
    <SelectionToolbar
      ariaLabel="File selection actions"
      onAnnotateSelection={onAnnotateSelection}
      onAskSelectionInSideChat={onAskSelectionInSideChat}
      onDismiss={onDismiss}
      onMoreDetailsSelection={onMoreDetailsSelection}
      selection={selection}
      source={{ sourceId: filePath, sourceKind: 'file', sourceLabel: filePath }}
    />
  );
}
